/**
 * Shared props/helpers for the three segment text tabs (`SegmentTextTab_*`).
 *
 * The tabs each own one `TextArea` over one `JournalSegment` text field. All three write through the
 * same path (quick-backup + clone segments + `UpdateJournalEntry`), so that logic lives here instead
 * of being copy-pasted three times -- and so a future change (eg. debounced saves) happens once.
 *
 * Not a component itself, so it lives beside them without being a `.tsx`.
 */
import {Clone} from "js-vextensions";
import {JournalEntry, JournalSegment} from "../../../../../Server/DB/journalEntries/@JournalEntry.js";
import {UpdateJournalEntry} from "../../../../../Server/Commands/JournalEntryCommands.js";
import {Clone_KeepTypeDef} from "../../../../../Utils/General/General.js";
import {GetWordsInText_Array} from "../../../../../Utils/Search/StringToWords.js";

export type SegmentTextTabProps = {
	entry: JournalEntry;
	segment: JournalSegment;
	segmentIndex: number;
};

/** Word counts for the segment's three text fields, shown in the tab buttons ("Public text (135)").
 *
 * Uses the project's own word tokenizer (rather than splitting on whitespace) so the number matches
 * what the rest of the app calls a "word". Timestamps are excluded from the long-text count, because
 * a transcription is mostly `[hh:mm:ss]` markers and counting those would inflate it.
 *
 * Deliberately NOT deduped: this is a length hint, not a vocabulary measure. */
export function GetSegmentTextWordCounts(segment: JournalSegment): {shortText: number, longText: number, publicText: number} {
	return {
		shortText: GetWordsInText_Array(segment.shortText ?? "", false).length,
		longText: GetWordsInText_Array(segment.longText ?? "", false, true, true).length,
		publicText: GetWordsInText_Array(segment.publicText ?? "", false).length,
	};
}

export const journalSegment_quickBackups = [] as JournalSegment[];
/** This is just a last line of defense against the case of someone accidentally deleting the wrong segment, or accidentally overwriting its text.
(eg. due to text-area glitch when selected segment changes but some flaw makes it still hold the old segment's text/edited-text)
In the future, there should be some sort of "trash"/recovery system (accessible from UI, rather than dev-tools like this) to handle these mistakes. */
export function QuickBackupJournalSegment(oldData: JournalSegment) {
	journalSegment_quickBackups.push(Clone(oldData));
}

/** Writes one text field of a segment, after taking a quick-backup and preserving the segment's
 * prototype (so its computed getters/methods survive the round-trip through the DB). */
export function UpdateSegmentTextField(props: SegmentTextTabProps, field: "shortText"|"longText"|"publicText", value: string) {
	const {entry, segment, segmentIndex} = props;
	QuickBackupJournalSegment(segment);
	const segments_new = Clone_KeepTypeDef(entry.segments);
	segments_new[segmentIndex] = segment.Extended({[field]: value});
	new UpdateJournalEntry({id: entry.id, updates: {segments: segments_new}}).RunOnServer();
}
